import type { Href } from "expo-router";
import { router } from "expo-router";
import { Linking, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { BOOKING_URL } from "../../../../../lib/config";
import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Client;
const GOLD = COLORS.GOLD_RM.hex;
const AMBER = THEME.accent;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const LINEN = THEME.textOnSurface;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const BORDER = "rgba(255,255,255,0.07)";

const PROCESS: { n: string; title: string; body: string }[] = [
  {
    n: "1",
    title: "Discovery",
    body: "60-minute video call. We hear what you need and scope the program.",
  },
  {
    n: "2",
    title: "Custom case design",
    body: "Coravu drafts the brief, deliverables, schedule, and rubric — no payment until you approve.",
  },
  {
    n: "3",
    title: "Onboarding & contract",
    body: "We finalize the program, you sign, and we onboard your team to the portal.",
  },
  {
    n: "4",
    title: "Launch",
    body: "Coravu locks in the venue and student outreach. The program goes live.",
  },
];

export default function BookDiscoveryCallScreen() {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  function openExternal() {
    if (Platform.OS === "web") {
      if (typeof window !== "undefined") window.open(BOOKING_URL, "_blank");
    } else {
      void Linking.openURL(BOOKING_URL);
    }
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: 80 }}
    >
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: 980 }}>
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/client/competition/new" as Href);
          }}
          style={[cursorWeb, { alignSelf: "flex-start", paddingVertical: 4 }]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyMedium,
              fontSize: 11.5,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: WM,
            }}
          >
            {"← Back to engagement picker"}
          </Text>
        </Pressable>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 14 }}>
          <View style={{ width: 22, height: 2, backgroundColor: GOLD, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            Coravu Managed · Discovery
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.display,
            fontSize: 36,
            lineHeight: 40,
            color: W,
          }}
        >
          Pick a time with <Text style={{ fontStyle: "italic", color: GOLD }}>Tyler</Text>.
        </Text>
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.bodyLight,
            fontSize: 14,
            lineHeight: 22,
            color: WS,
            maxWidth: 760,
          }}
        >
          {"60-minute video call. We'll scope your program, target talent profile, timeline, and budget. No commitment. Tyler or your Coravu rep walks you through everything."}
        </Text>

        {/* Process strip */}
        <View
          style={{
            marginTop: 24,
            padding: 22,
            borderRadius: 6,
            backgroundColor: "rgba(201,162,74,0.04)",
            borderWidth: 1,
            borderColor: "rgba(201,162,74,0.20)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            What happens after the call
          </Text>
          <View
            style={{
              marginTop: 14,
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            {PROCESS.map((step) => (
              <View
                key={step.n}
                style={{
                  flexBasis: 200,
                  flexGrow: 1,
                  flexShrink: 1,
                  paddingHorizontal: 14,
                  paddingVertical: 14,
                  borderRadius: 4,
                  backgroundColor: "rgba(0,0,0,0.25)",
                  borderWidth: 1,
                  borderColor: "rgba(201,162,74,0.18)",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 18,
                    color: GOLD,
                  }}
                >
                  {step.n}
                </Text>
                <Text
                  style={{
                    marginTop: 6,
                    fontFamily: FONTS.displayMedium,
                    fontSize: 17,
                    lineHeight: 21,
                    color: W,
                  }}
                >
                  {step.title}
                </Text>
                <Text
                  style={{
                    marginTop: 6,
                    fontFamily: FONTS.body,
                    fontSize: 11.5,
                    lineHeight: 17,
                    color: WM,
                  }}
                >
                  {step.body}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Primary CTA card. Microsoft Bookings ships X-Frame-Options:
            DENY, so embedding it in an iframe is permanently blocked —
            we send users to a new tab instead. */}
        <View
          style={{
            marginTop: 22,
            padding: 40,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 22,
              lineHeight: 28,
              color: LINEN,
              textAlign: "center",
            }}
          >
            Open the booking page
          </Text>
          <Text
            style={{
              marginTop: 10,
              maxWidth: 480,
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 22,
              color: WS,
              textAlign: "center",
            }}
          >
            {"Microsoft Bookings opens in a new tab. Pick a time, and you'll get a Teams link plus a calendar invite by email. No Microsoft account needed."}
          </Text>
          <Pressable
            onPress={openExternal}
            style={[
              {
                marginTop: 22,
                height: 48,
                paddingHorizontal: 32,
                borderRadius: 999,
                backgroundColor: AMBER,
                alignItems: "center",
                justifyContent: "center",
              } as ViewStyle,
              cursorWeb,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 12,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: FOREST,
              }}
            >
              {"Open booking page →"}
            </Text>
          </Pressable>

          <View style={{ marginTop: 22, alignSelf: "stretch", gap: 8 }}>
            <ReassureRow>
              {"Tyler's calendar drives availability — only free slots show up."}
            </ReassureRow>
            <ReassureRow>
              {"We'll send a calendar invite you can add to Outlook, Google, or Apple Calendar."}
            </ReassureRow>
            <ReassureRow>
              {"Join via Microsoft Teams (no Microsoft account needed)."}
            </ReassureRow>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

function ReassureRow({ children }: { children: string }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 13,
          color: COLORS.SAGE.hex,
          lineHeight: 18,
        }}
      >
        ✓
      </Text>
      <Text
        style={{
          flex: 1,
          fontFamily: FONTS.body,
          fontSize: 12.5,
          lineHeight: 18,
          color: WS,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

